"use client";

import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { pricingData, PRO_EXTRA_BENEFITS } from "@/config/subscriptions";
import { useOnboardingMode } from "@/hooks/use-onboarding-mode";
import { Icons } from "@/components/shared/icons";
import { useSession } from "next-auth/react";
import { cn } from "@/lib/utils";
import { trackClientEvent } from "@/lib/analyticsClient";
import { trackPreRegUsage } from "@/lib/sandbox-onboarding";

export function OnboardingPricingModal() {
  const { showPricing, hadPreviousSubscription } = useOnboardingMode();
  const [isYearly, setIsYearly] = useState(false);
  const { data: session } = useSession();
  const pathname = usePathname();
  const trackedRef = useRef(false);
  const activationPath = pathname?.startsWith("/dashboard/gex") ? "gex" : "pro";

  useEffect(() => {
    if (!showPricing || trackedRef.current) return;
    trackedRef.current = true;
    trackPreRegUsage(`activation_screen:${activationPath}`);
    trackClientEvent({
      name: "activation_screen_viewed",
      params: { unused_income_amount: 0, path: activationPath, first_time_activation_viewed: !hadPreviousSubscription },
      userId: session?.user?.id ?? undefined,
    });
    trackClientEvent({
      name: "prices_shown",
      params: { source: pathname || "unknown" },
      userId: session?.user?.id ?? undefined,
    });
  }, [activationPath, hadPreviousSubscription, showPricing, session?.user?.id, pathname]);

  const gexPlan = pricingData.find((p) => p.title === "QuantWheel GEX")!;
  const proPlan = pricingData.find((p) => p.title === "QuantWheel PRO")!;

  const toggleBilling = (value: string) => {
    if (value === "yearly") setIsYearly(true);
    else if (value === "monthly") setIsYearly(false);
  };

  return (
    <Dialog open={showPricing} onOpenChange={() => {}}>
      <DialogContent
        className="max-w-2xl p-0 gap-0 overflow-visible border-0 bg-transparent shadow-none [&>button]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogTitle className="sr-only">Choose your plan</DialogTitle>
        <div className="flex justify-center p-4">
          <div className="w-full max-w-2xl rounded-lg overflow-hidden shadow-xl">
            <div className="h-1.5 rounded-t-lg bg-gradient-to-r from-transparent via-zinc-300 dark:via-zinc-600 to-transparent" />
            <div className="rounded-b-lg border border-t-0 border-border/50 bg-background shadow-lg">
              <div className="p-6 sm:p-8 space-y-6">
                <div className="space-y-2 text-center">
                  <h2 className="text-xl font-semibold tracking-tight">
                    Choose your plan
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    Start with GEX or unlock the full platform with PRO. Cancel anytime.
                  </p>
                </div>

                <div className="flex justify-center">
                  <ToggleGroup
                    type="single"
                    value={isYearly ? "yearly" : "monthly"}
                    onValueChange={toggleBilling}
                    className="h-9 rounded-full bg-muted p-1"
                  >
                    <ToggleGroupItem
                      value="monthly"
                      className="h-7 rounded-full px-4 text-xs data-[state=on]:bg-background data-[state=on]:shadow-sm"
                    >
                      Monthly
                    </ToggleGroupItem>
                    <ToggleGroupItem
                      value="yearly"
                      className="h-7 rounded-full px-4 text-xs data-[state=on]:bg-background data-[state=on]:shadow-sm"
                    >
                      Yearly <span className="ml-1 font-semibold text-emerald-500">(-25%)</span>
                    </ToggleGroupItem>
                  </ToggleGroup>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  {[gexPlan, proPlan].map((plan) => {
                    const isPro = plan.title === "QuantWheel PRO";
                    const isGex = plan.title === "QuantWheel GEX";
                    const yearlySavings = plan.prices.monthly * 12 - plan.prices.yearly;
                    const planSlug = plan.title.toLowerCase().replace("quantwheel ", "");

                    const benefits = isPro
                      ? PRO_EXTRA_BENEFITS.slice(0, 5)
                      : plan.benefits.slice(0, 6);

                    return (
                      <div
                        key={plan.title}
                        className={cn(
                          "relative rounded-xl border p-5",
                          "bg-gradient-to-r from-zinc-100/70 via-background to-zinc-100/50 dark:from-zinc-900/80 dark:via-background dark:to-zinc-900/60",
                          isPro ? "border-primary ring-1 ring-primary/30" : "border-border/50",
                        )}
                      >
                        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-zinc-300 dark:via-zinc-600 to-transparent" />
                        {isPro && (
                          <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-[10px] font-semibold px-2.5 py-0.5 rounded-full">
                            MOST POPULAR
                          </span>
                        )}

                        <div className="space-y-4">
                        <div>
                          <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                            QuantWheel{" "}
                            <span className={cn(
                              isPro && "text-primary drop-shadow-[0_0_6px_hsl(var(--primary)/0.4)]",
                              isGex && "text-zinc-500 dark:text-zinc-300",
                            )}>
                              {isPro ? "PRO" : "GEX"}
                            </span>
                          </p>
                          <p className="text-[10px] text-muted-foreground/70 mt-0.5">
                            {isGex ? "For directional traders" : "For Wheel and Option Sellers"}
                          </p>
                          <div className="flex items-baseline gap-1 mt-1">
                            {isYearly && (
                              <span className="text-lg text-muted-foreground/60 line-through">
                                ${plan.prices.monthly}
                              </span>
                            )}
                            <span className="text-3xl font-bold">
                              ${isYearly ? Math.floor(plan.prices.yearly / 12) : plan.prices.monthly}
                            </span>
                            <span className="text-sm text-muted-foreground">/mo</span>
                          </div>
                          {isYearly && yearlySavings > 0 && (
                            <p className="text-xs text-emerald-500 mt-0.5">
                              Save ${yearlySavings}/year
                            </p>
                          )}
                        </div>

                        <ul className="space-y-1.5 text-xs">
                          {benefits.map((b) => (
                            <li key={b} className="flex items-start gap-2">
                              <Icons.check className="h-3.5 w-3.5 shrink-0 text-primary mt-0.5" />
                              <span className="text-muted-foreground">{b}</span>
                            </li>
                          ))}
                          {isPro && (
                            <>
                              {PRO_EXTRA_BENEFITS.length > 5 && (
                                <li>
                                  <Link href="/pricing" className="text-muted-foreground/60 hover:text-primary transition-colors">
                                    +{PRO_EXTRA_BENEFITS.length - 5} more features →
                                  </Link>
                                </li>
                              )}
                              <li className="flex items-start gap-2 pt-1.5 border-t border-border/40">
                                <Icons.check className="h-3.5 w-3.5 shrink-0 text-zinc-400 dark:text-zinc-500 mt-0.5" />
                                <span className="font-semibold text-foreground">Everything in QuantWheel GEX</span>
                              </li>
                            </>
                          )}
                        </ul>

                        <Button
                          size="lg"
                          className="w-full bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:from-emerald-600 hover:to-green-700 shadow-sm font-semibold"
                          onClick={() => {
                            const billingCycle = isYearly ? "yearly" : "monthly";
                            const intent = `${planSlug}|${billingCycle}`;
                            try { sessionStorage.setItem("trialIntent", intent); } catch {}
                            if (session?.user?.id) {
                              // Already signed in: go straight to Stripe checkout
                              window.location.href = `/auth/callback?intent=${encodeURIComponent(intent)}`;
                            } else {
                              // Encode the trial intent into the login callback so that after
                              // Google or email sign-in the user lands directly in Stripe checkout
                              window.location.href = `/login?callbackUrl=${encodeURIComponent(`/auth/callback?intent=${intent}`)}`;
                            }
                          }}
                        >
                          {hadPreviousSubscription ? "Choose this plan" : "Start 7 Days for $1"}
                        </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <p className="text-center text-xs text-muted-foreground">
                  After starting your trial you&apos;ll connect your broker in one
                  click and see the unused income potential of your portfolio.
                </p>

              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
